import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { plural, t } from '@lingui/macro';
import styled from 'styled-components';
import rem from 'polished/lib/helpers/rem';

import Alert from '@decisiv/ui-components/lib/components/Alert';
import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import KDSTagInput, {
  TagItem,
} from '@decisiv/ui-components/lib/components/TagInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import QuestionCircle from '@decisiv/iconix/lib/components/QuestionCircle';
import Times from '@decisiv/iconix/lib/components/Times';

import { transformTags } from '../utils/transformTags';
import { MAX_VINS } from './schema';

// The mockup calls for a taller VIN entry area (~100px) so several VINs are
// visible as they wrap. KDS TagInput's box defaults to a 50px min-height;
// raise it and top-align the content so tags fill from the top.
const TagInputWrapper = styled.div`
  & > div > div > div:first-of-type {
    min-height: ${rem(100)};
    align-items: flex-start;
  }
`;

const getTagText = (tag: TagItem): string =>
  typeof tag === 'string' ? tag : tag.text;

const dedupeNewTags = (
  existingTexts: Set<string>,
  newTags: TagItem[],
): { deduped: TagItem[]; duplicateCount: number } =>
  newTags.reduce(
    (acc, tag) => {
      const text = getTagText(tag);
      if (acc.seen.has(text)) {
        return { ...acc, duplicateCount: acc.duplicateCount + 1 };
      }
      acc.seen.add(text);
      return { ...acc, deduped: [...acc.deduped, tag] };
    },
    {
      seen: new Set<string>(existingTexts),
      deduped: [] as TagItem[],
      duplicateCount: 0,
    },
  );

const VinTagInput = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const [clearKey, setClearKey] = React.useState(0);
  const [duplicateCount, setDuplicateCount] = React.useState(0);
  const [showLimitWarning, setShowLimitWarning] = React.useState(false);

  // Only the first MAX_VINS entries are accepted in the manual flow. Anything
  // beyond that is dropped and the user is pointed to the Bulk Add wizard.
  const capToLimit = (tags: TagItem[]): TagItem[] => {
    if (tags.length <= MAX_VINS) return tags;
    setShowLimitWarning(true);
    return tags.slice(0, MAX_VINS);
  };

  return (
    <Controller
      name="vins"
      control={control}
      render={({ field: { onChange, value } }) => {
        const existing: TagItem[] = value ?? [];
        const existingTexts = new Set<string>(existing.map(getTagText));

        const commitInput = (inputValue: string) => {
          const newTags = transformTags([inputValue]);
          const { deduped, duplicateCount: removedDuplicateCount } =
            dedupeNewTags(existingTexts, newTags);

          if (removedDuplicateCount > 0) {
            setDuplicateCount(removedDuplicateCount);
          }

          /* istanbul ignore next */
          if (removedDuplicateCount === 0 && deduped.length > 0) {
            setDuplicateCount(0);
          }

          if (deduped.length > 0)
            onChange(capToLimit([...existing, ...deduped]));
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key !== 'Enter' && e.key !== 'Tab') return;

          const input = e.target as HTMLInputElement;
          const inputValue = input.value.trim();

          if (e.key === 'Enter') e.preventDefault();
          if (!inputValue) return;
          if (e.key === 'Tab') e.preventDefault();

          commitInput(inputValue);
          input.value = '';
        };

        const handleTagChange = (tags: TagItem[]) => {
          if (tags.length < existing.length) {
            // A removal always brings the list back within the cap, so clear
            // the truncation warning rather than let it linger stale.
            setShowLimitWarning(false);
            onChange(tags);
            return;
          }

          const transformedTags = transformTags(tags);
          const newTags = transformedTags.slice(existing.length);
          const { deduped, duplicateCount: removedDuplicateCount } =
            dedupeNewTags(existingTexts, newTags);

          if (removedDuplicateCount > 0) {
            setDuplicateCount(removedDuplicateCount);
          }

          /* istanbul ignore next */
          if (removedDuplicateCount === 0 && deduped.length > 0) {
            setDuplicateCount(0);
          }

          onChange(capToLimit([...existing, ...deduped]));
        };

        const helpMessage = t`Max amount ${MAX_VINS} VINs.`;

        return (
          <Flex flexDirection="column">
            {showLimitWarning && (
              <Flex marginBottom={0.5}>
                <Alert
                  title={t`Only the first ${MAX_VINS} VINs were added. To add more, use the Bulk Add Assets option.`}
                  intent="warning"
                  dismissable
                  onDismissClick={() => setShowLimitWarning(false)}
                />
              </Flex>
            )}
            {duplicateCount > 0 && (
              <Flex marginBottom={0.5}>
                <Alert
                  title={plural(duplicateCount, {
                    one: '# duplicate VIN was removed.',
                    other: '# duplicate VINs were removed.',
                  })}
                  intent="warning"
                  dismissable
                  onDismissClick={() => setDuplicateCount(0)}
                />
              </Flex>
            )}
            <TagInputWrapper>
              <KDSTagInput
                key={clearKey}
                tags={value}
                onChange={handleTagChange}
                onKeyDown={handleKeyDown}
                warningMessage={errors.vins?.message?.toString()}
                label={t`VIN`}
                maxTags={100}
                options={[]}
                required
                showMaxTagsCount
              />
            </TagInputWrapper>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              marginTop={0.5}
            >
              <Flex alignItems="center">
                <QuestionCircle size="small" color="alaskanHusky" />
                <P size="small" shade={1} marginLeft={0.5}>
                  {helpMessage}
                </P>
              </Flex>
              <Button
                text={t`Clear`}
                size="small"
                variant="ghost"
                icon={Times}
                type="button"
                disabled={existing.length === 0}
                onClick={() => {
                  onChange([]);
                  setClearKey((k) => k + 1);
                  setShowLimitWarning(false);
                }}
              />
            </Flex>
          </Flex>
        );
      }}
    />
  );
};

export default VinTagInput;
